import { Query } from '@varo/graphql-schema-types';
import { useCallback, useEffect, useRef, useState } from 'react';
import {
  FetchHomeTilesForIds,
  FetchHomeTilesV2,
  RefetchSystemBanners,
} from 'services/graphql/queries/FetchHomeTilesV2';
import homeTileTypes from 'shared/constants/homeTileTypes';
import { OperationContext, useQuery } from 'urql';

/**
 * Performs a full screen refresh of the home tiles.
 *
 * NOTE: DO NOT USE THIS UNLESS YOU HAVE TO!
 *
 * to refresh a tile use `useRefreshHomeTiles` instead.
 * this hook should only be used in the case where you are updating
 * home after performing an action that adds a tile or changes the
 * ordering of a tile.
 */
export const useHomeTilesFullScreenRefresh = () => {
  const [response, refreshTiles] = useQuery<Query>({
    query: FetchHomeTilesV2,
    pause: true,
    variables: {
      tileTypes: homeTileTypes,
    },
    requestPolicy: 'network-only',
  });

  const { data, error, fetching } = response;
  return {
    data,
    error,
    fetching,
    refreshTiles,
  };
};

/**
 * Use this mutation to refresh all system banners
 * This query fields identical to the FetchHomeTilesV2 query.
 * @returns query operation information and a function to refresh the tile ids provided
 */
export const useRefreshSystemBanners = () => {
  const [response, refreshSystemBanners] = useQuery<Query>({
    query: RefetchSystemBanners,
    pause: true,
    requestPolicy: 'network-only',
    variables: {
      ids: [],
      tileTypes: [],
    },
  });

  const { data, error, fetching } = response;

  return {
    data,
    error,
    fetching,
    refreshSystemBanners,
  };
};

/**
 * Use this mutation to refresh specific tile ids in the cache.
 * This query fields identical to the FetchHomeTilesV2 query.
 * @param ids the tile ids to refresh
 * @returns query operation information and a function to refresh the tile ids provided
 */
const useRefreshHomeTiles = (ids?: string[]) => {
  const [tileIds, setTileIds] = useState<string[] | undefined>(ids);

  const queueRefetch = useRef(false);

  const [response, fetchQuery] = useQuery<Query>({
    query: FetchHomeTilesForIds,
    pause: true,
    requestPolicy: 'network-only',
    variables: {
      ids: tileIds,
      tileTypes: homeTileTypes,
    },
  });

  const { data, error, fetching } = response;

  const refreshTiles = useCallback(
    (options?: Partial<OperationContext> | string[]) => {
      if (options && typeof options === 'object') {
        if (Array.isArray(options)) {
          queueRefetch.current = true;
          setTileIds(options);
        } else {
          fetchQuery(options);
        }
      } else {
        fetchQuery();
      }
    },
    [fetchQuery],
  );

  // Fire the queued fetchQuery after tileIds state update has been applied.
  useEffect(() => {
    if (queueRefetch.current) {
      queueRefetch.current = false;
      fetchQuery({ networkPolicy: 'network-only' });
    }
  }, [tileIds, fetchQuery]);

  return {
    data,
    error,
    fetching,
    refreshTiles,
  };
};

export default useRefreshHomeTiles;
